import React, { useCallback, useRef, useState } from "react";
import type { Ref } from "react";
import type { LayoutChangeEvent } from "react-native";
import { View } from "react-native";

import Image from "$src/core/components/atoms/Image/Image";
import Text from "$src/core/components/atoms/Text/Text";
import TVFocusGuideView from "$src/core/components/atoms/TVFocusGuideView";
import SwimmingLane from "$src/core/components/organisms/SwimmingLane";
import SwimmingLanePreviousOverlay from "$src/core/components/organisms/SwimmingLane/SwimmingLanePreviousOverlay";
import type {
  SwimmingLaneRef,
  SwimmingLaneScrollAbilities,
} from "$src/core/components/organisms/SwimmingLane/types";
import useResponsiveImage from "$src/core/hooks/use-responsive-image";
import type { LaneSectionDto } from "$src/core/models/dto";
import $pageInsets from "$src/core/styles/page";

import $, { IMAGE_DIMENSIONS } from "./LaneSection.styles";

export type LaneSectionProps = {
  ref?: Ref<SwimmingLaneRef>;
  item: LaneSectionDto;
};

const LaneSection = ({ ref, item }: LaneSectionProps) => {
  const [brandImageWidth, setBrandImageWidth] = useState<number | undefined>(
    undefined,
  );
  const [canScrollPrev, setCanScrollPrev] = useState<boolean>(false);
  const scrollPreviousRef = useRef<() => void>(() => {});

  const handleScrollAbilitiesChange = useCallback(
    (state: SwimmingLaneScrollAbilities) => {
      setCanScrollPrev(state.canScrollPrev);
      scrollPreviousRef.current = state.scrollPrevious;
    },
    [],
  );

  const responsiveBrandImage = useResponsiveImage(item.brandImage?.url, {
    dimensions: [{ width: brandImageWidth }],
  });
  const responsiveBrandLogoImage = useResponsiveImage(item.brandLogo?.url, {
    dimensions: IMAGE_DIMENSIONS.brandLogo,
  });

  const handleBrandImageLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setBrandImageWidth(width);
  }, []);

  const isBranded = !!item.brandImage;

  if (isBranded) {
    const brandedImageContentOffset = brandImageWidth ? brandImageWidth / 2 : 0;

    return (
      <TVFocusGuideView
        style={[
          $.section,
          { backgroundColor: item.brandColor },
          $pageInsets.left,
        ]}
        autoFocus
      >
        <View
          style={[
            $.sectionHeader,
            { marginLeft: brandedImageContentOffset * 2 },
            $pageInsets.right,
          ]}
        >
          <Text preset="heading2" accessibilityRole="header" numberOfLines={1}>
            {item.title}
          </Text>
          {item.brandLogo && (
            // This is a decorative image, so we don't need an accessibility label
            // eslint-disable-next-line triple-rn-a11y/accessibility-label
            <Image
              source={responsiveBrandLogoImage}
              contentFit="contain"
              accessibilityRole="none"
              style={$.sectionHeaderBrandLogo}
            />
          )}
        </View>
        <View>
          <SwimmingLane
            ref={ref}
            items={item.items}
            type={item.aspect}
            style={[
              $.sectionLane,
              {
                marginLeft: brandedImageContentOffset,
                paddingLeft: brandedImageContentOffset,
              },
            ]}
            accessibilityLaneTitle={item.title}
            contentContainerStyle={[$.sectionLaneContent, $pageInsets.right]}
            hidePreviousOverlay
            onScrollAbilitiesChange={handleScrollAbilitiesChange}
            focusOffset={-brandedImageContentOffset}
          />
          {item.brandImage && (
            <View style={$.brandImageContainer}>
              {/* This is a decorative image, so we don't need an accessibility label */}
              {/* eslint-disable-next-line triple-rn-a11y/accessibility-label */}
              <Image
                accessibilityRole="none"
                source={responsiveBrandImage}
                contentFit="contain"
                onLayout={handleBrandImageLayout}
                pointerEvents="none"
                style={$.brandImage}
              />
            </View>
          )}
          <SwimmingLanePreviousOverlay
            visible={canScrollPrev}
            onPress={() => scrollPreviousRef.current()}
            style={[{ marginLeft: brandedImageContentOffset * 2 }]}
          />
        </View>
      </TVFocusGuideView>
    );
  }

  return (
    <View style={$.section}>
      <View style={[$.sectionHeader, $pageInsets.horizontal]}>
        <Text preset="heading2" accessibilityRole="header" numberOfLines={1}>
          {item.title}
        </Text>
      </View>
      <SwimmingLane
        ref={ref}
        items={item.items}
        type={item.aspect}
        accessibilityLaneTitle={item.title}
        contentContainerStyle={[$.sectionLaneContent, $pageInsets.horizontal]}
      />
    </View>
  );
};

export default LaneSection;
